import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { TrendingUp, BarChart3, Calendar } from 'lucide-react'
import api from '@/services/api'

interface DailyMonthlyStats {
  periodStart: string
  periodEnd: string
  total?: number
  diasNoPeriodo?: number
  cadastrosPorDia?: number
  diasComCadastro?: number
  totalLeadsUnicos?: number
  diasComAtividade: number
  mesesComAtividade: number
  mediaDia?: number
  mediaMes?: number
  mediaLeadsPorDia?: number
  mediaLeadsPorMes?: number
}

interface PerformanceData {
  period: { start: string; end: string }
  hasPeriodFilter?: boolean
  carteiraTotal?: number
  cadastro?: DailyMonthlyStats
  contato?: DailyMonthlyStats
  leadsNaCarteira: number
  leadsTrabalhados: number
  reunioesRealizadas: number
  leadsComReuniao?: number
  conversionRate: number
  attemptsByChannel: { canal: string; count: number }[]
  bySegment: { segmento: string; count: number }[]
}

export function BdrPerformanceReport({ bdrId }: { bdrId: string }) {
  const [data, setData] = useState<PerformanceData | null>(null)
  const [loading, setLoading] = useState(true)
  const [dateStart, setDateStart] = useState('')
  const [dateEnd, setDateEnd] = useState('')

  const hasPeriodFilter = Boolean(dateStart || dateEnd)
  const today = new Date().toISOString().slice(0, 10)

  function handleDateStartChange(value: string) {
    setDateStart(value)
    if (!value) {
      setDateEnd('')
      return
    }
    if (dateEnd && dateEnd < value) {
      setDateEnd(value)
    }
  }

  function handleDateEndChange(value: string) {
    if (value && dateStart && value < dateStart) {
      setDateEnd(dateStart)
      return
    }
    setDateEnd(value)
  }

  useEffect(() => {
    if (!bdrId) return
    async function load() {
      setLoading(true)
      try {
        const params: Record<string, string> = { bdrId }
        if (dateStart) params.dateStart = dateStart
        if (dateEnd) params.dateEnd = dateEnd
        const { data: res } = await api.get('/bdr/performance-report', { params })
        const raw = res.data ?? res
        setData({
          ...raw,
          hasPeriodFilter: raw.hasPeriodFilter ?? hasPeriodFilter,
          carteiraTotal: raw.carteiraTotal ?? raw.leadsNaCarteira ?? 0,
          leadsNaCarteira: raw.leadsNaCarteira ?? 0,
          leadsTrabalhados: raw.leadsTrabalhados ?? raw.prospected ?? 0,
          reunioesRealizadas: raw.reunioesRealizadas ?? raw.reunioesAgendadas ?? 0,
        })
      } catch {
        // silent
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [bdrId, dateStart, dateEnd])

  function formatDate(iso: string) {
    return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: '2-digit' })
  }

  const maxChannel = Math.max(...(data?.attemptsByChannel.map((c) => c.count) ?? [1]), 1)
  const maxSegment = Math.max(...(data?.bySegment.map((s) => s.count) ?? [1]), 1)

  const CANAL_COLORS: Record<string, string> = {
    Telefone: '#3B82F6',
    WhatsApp: '#10B981',
    Email: '#F59E0B',
    'Multi-canal': '#8B5CF6',
  }

  return (
    <div className="glass-card border-white/[0.06] p-5">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2 text-text-primary font-extrabold text-xs uppercase tracking-widest">
          <BarChart3 size={15} className="text-accent shrink-0" />
          Relatório de Performance
        </div>

        <div className="flex items-center gap-2">
          <input
            type="date"
            value={dateStart}
            max={dateEnd || today}
            onChange={(e) => handleDateStartChange(e.target.value)}
            aria-label="Data inicial do período"
            className="px-2 py-1 text-[10px] bg-white/[0.04] border border-white/[0.06] rounded-lg text-text-primary focus:outline-none focus:border-accent/30"
          />
          <span className="text-[10px] text-text-muted">até</span>
          <input
            type="date"
            value={dateEnd}
            min={dateStart || undefined}
            max={today}
            disabled={!dateStart}
            onChange={(e) => handleDateEndChange(e.target.value)}
            aria-label="Data final do período"
            title={dateStart ? undefined : 'Selecione a data inicial primeiro'}
            className="px-2 py-1 text-[10px] bg-white/[0.04] border border-white/[0.06] rounded-lg text-text-primary focus:outline-none focus:border-accent/30 disabled:cursor-not-allowed disabled:opacity-40"
          />
        </div>
      </div>

      {loading ? (
        <div className="space-y-3 animate-pulse">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-10 bg-white/[0.04] rounded-lg" />
          ))}
        </div>
      ) : !data ? (
        <div className="text-center py-8">
          <TrendingUp size={24} className="mx-auto text-text-muted mb-2" />
          <p className="text-xs text-text-muted">Sem dados no período</p>
        </div>
      ) : (
        <div className="space-y-5">
          {/* Summary Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="p-3 bg-white/[0.02] border border-white/[0.04] rounded-xl text-center">
              <div className="text-xl font-extrabold text-text-primary">{data.leadsNaCarteira}</div>
              <div className="text-[9px] font-bold text-text-muted uppercase tracking-wider">
                {(data.hasPeriodFilter ?? hasPeriodFilter) ? 'Cadastrados no período' : 'Leads na Carteira'}
              </div>
              {(data.hasPeriodFilter ?? hasPeriodFilter) && (data.carteiraTotal ?? 0) > 0 && (
                <p className="text-[8px] text-text-muted mt-1">{data.carteiraTotal} na carteira total</p>
              )}
            </div>
            <div className="p-3 bg-blue-500/5 border border-blue-500/10 rounded-xl text-center">
              <div className="text-xl font-extrabold text-blue-400">{data.leadsTrabalhados}</div>
              <div className="text-[9px] font-bold text-blue-400/80 uppercase tracking-wider">Leads Trabalhados</div>
              {(data.hasPeriodFilter ?? hasPeriodFilter) ? (
                <p className="text-[8px] text-text-muted mt-1">com tentativa no período</p>
              ) : data.leadsNaCarteira > data.leadsTrabalhados ? (
                <p className="text-[8px] text-text-muted mt-1">
                  +{data.leadsNaCarteira - data.leadsTrabalhados} na carteira sem tentativa
                </p>
              ) : null}
            </div>
            <div className="p-3 bg-emerald-500/5 border border-emerald-500/10 rounded-xl text-center">
              <div className="text-xl font-extrabold text-emerald-400">{data.reunioesRealizadas}</div>
              <div className="text-[9px] font-bold text-emerald-400/80 uppercase tracking-wider">Reuniões Realizadas</div>
              {(data.hasPeriodFilter ?? hasPeriodFilter) && (
                <p className="text-[8px] text-text-muted mt-1">realizadas no período</p>
              )}
            </div>
            <div className="p-3 bg-accent/5 border border-accent/10 rounded-xl text-center">
              <div className="text-xl font-extrabold text-accent">{data.conversionRate}%</div>
              <div className="text-[9px] font-bold text-accent/80 uppercase tracking-wider">Taxa Reunião</div>
              <p className="text-[8px] text-text-muted mt-1">sobre leads trabalhados</p>
            </div>
          </div>

          {data.cadastro && (data.cadastro.total ?? 0) > 0 && (
            <div>
              <div className="text-[10px] font-bold text-text-muted uppercase tracking-wider mb-2">Cadastros (data de entrada)</div>
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-violet-500/5 border border-violet-500/10 rounded-xl text-center">
                  <div className="text-xl font-extrabold text-violet-300">{data.cadastro.total}</div>
                  <div className="text-[9px] font-bold text-violet-300/80 uppercase tracking-wider">Cadastros realizados</div>
                  <p className="text-[8px] text-text-muted mt-1">no período selecionado</p>
                </div>
                <div className="p-3 bg-violet-500/5 border border-violet-500/10 rounded-xl text-center">
                  <div className="text-xl font-extrabold text-violet-300">
                    {data.cadastro.cadastrosPorDia ?? data.cadastro.mediaDia}
                  </div>
                  <div className="text-[9px] font-bold text-violet-300/80 uppercase tracking-wider">Cadastros por dia</div>
                  <p className="text-[8px] text-text-muted mt-1">
                    {data.cadastro.diasNoPeriodo ?? '—'} dias no período
                    {(data.cadastro.diasComCadastro ?? data.cadastro.diasComAtividade) > 0 && (
                      <> · {data.cadastro.diasComCadastro ?? data.cadastro.diasComAtividade} com registro</>
                    )}
                  </p>
                </div>
              </div>
            </div>
          )}

          {data.contato && data.contato.diasComAtividade > 0 && (
            <div>
              <div className="text-[10px] font-bold text-text-muted uppercase tracking-wider mb-2">Contato (média nos dias/meses com tentativa registrada)</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <div className="p-3 bg-amber-500/5 border border-amber-500/10 rounded-xl text-center">
                  <div className="text-xl font-extrabold text-amber-300">{data.contato.totalLeadsUnicos ?? 0}</div>
                  <div className="text-[9px] font-bold text-amber-300/80 uppercase tracking-wider">Leads únicos contatados</div>
                </div>
                <div className="p-3 bg-amber-500/5 border border-amber-500/10 rounded-xl text-center">
                  <div className="text-xl font-extrabold text-amber-300">{data.contato.mediaLeadsPorDia}</div>
                  <div className="text-[9px] font-bold text-amber-300/80 uppercase tracking-wider">Média leads / dia ativo</div>
                  <p className="text-[8px] text-text-muted mt-1">{data.contato.diasComAtividade} dias com contato</p>
                </div>
                <div className="p-3 bg-amber-500/5 border border-amber-500/10 rounded-xl text-center">
                  <div className="text-xl font-extrabold text-amber-300">{data.contato.mediaLeadsPorMes}</div>
                  <div className="text-[9px] font-bold text-amber-300/80 uppercase tracking-wider">Média leads / mês ativo</div>
                  <p className="text-[8px] text-text-muted mt-1">{data.contato.mesesComAtividade} meses com contato</p>
                </div>
              </div>
            </div>
          )}

          {/* Attempts by Channel */}
          {data.attemptsByChannel.length > 0 && (
            <div>
              <div className="text-[10px] font-bold text-text-muted uppercase tracking-wider mb-2">Tentativas por Canal</div>
              <div className="space-y-2">
                {data.attemptsByChannel.map((ch) => (
                  <div key={ch.canal} className="flex items-center gap-3">
                    <span className="text-[10px] font-bold text-text-primary w-20 shrink-0">{ch.canal}</span>
                    <div className="flex-1 h-5 bg-white/[0.04] rounded-full overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${Math.max((ch.count / maxChannel) * 100, 4)}%` }}
                        transition={{ duration: 0.5 }}
                        className="h-full rounded-full"
                        style={{ backgroundColor: CANAL_COLORS[ch.canal] || '#6B7280' }}
                      />
                    </div>
                    <span className="text-[10px] font-bold text-text-primary w-8 text-right">{ch.count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* By Segment */}
          {data.bySegment.length > 0 && (
            <div>
              <div className="text-[10px] font-bold text-text-muted uppercase tracking-wider mb-2">Top Segmentos</div>
              <div className="flex flex-wrap gap-2">
                {data.bySegment.map((seg) => (
                  <div
                    key={seg.segmento}
                    className="flex items-center gap-1.5 px-2.5 py-1 bg-white/[0.03] border border-white/[0.06] rounded-lg"
                  >
                    <div
                      className="w-1.5 h-1.5 rounded-full"
                      style={{ backgroundColor: `hsl(${(seg.count / maxSegment) * 200}, 70%, 55%)` }}
                    />
                    <span className="text-[10px] font-bold text-text-primary">{seg.segmento}</span>
                    <span className="text-[9px] text-text-muted">({seg.count})</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="text-[9px] text-text-muted text-right space-y-0.5">
            <p>
              <Calendar size={9} className="inline mr-1" />
              Relatório: {formatDate(data.period.start)} — {formatDate(data.period.end)}
            </p>
            {data.cadastro && (
              <p>
                Cadastros (data entrada): {formatDate(data.cadastro.periodStart)} — {formatDate(data.cadastro.periodEnd)}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
